"use client";

import { Sparkles } from "lucide-react";
import Image from "next/image";

import heroDark from "@/assets/hero-dark.png";
import heroLight from "@/assets/hero-light.png";
import { AuthLink } from "@/components/shared/auth-link";
import { TrackedLink } from "@/components/shared/tracked-link";
import { useAppConfig } from "@/lib/config";

import { Button, Tag } from "@meetploy/storybook";

import { AnimatedGroup } from "./animated-group";
import { FeatureConsole } from "./feature-console";
import { Navbar } from "./navbar";

const transitionVariants = {
	item: {
		hidden: {
			opacity: 0,
			filter: "blur(12px)",
			y: 12,
		},
		visible: {
			opacity: 1,
			filter: "blur(0px)",
			y: 0,
			transition: {
				type: "spring",
				bounce: 0.3,
				duration: 1.5,
			},
		},
	},
};

export function Hero({
	navbarOnly,
	children,
}: {
	navbarOnly?: boolean;
	children?: React.ReactNode;
}) {
	const config = useAppConfig();

	return (
		<>
			<Navbar>{children}</Navbar>
			{!navbarOnly && (
				<main className="overflow-hidden">
					<section>
						<div className="relative pt-24 md:pt-36">
							<div
								aria-hidden
								className="absolute inset-0 -z-10 size-full bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:64px_64px] opacity-40 [mask-image:radial-gradient(ellipse_at_top,black_10%,transparent_70%)]"
							/>
							<div className="mx-auto max-w-7xl px-6">
								{/* 2-column grid layout */}
								<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
									{/* Column 1: Content */}
									<div className="text-center lg:text-left space-y-8">
										{/* Open source badge */}
										<AnimatedGroup variants={transitionVariants}>
											<TrackedLink
												href={config.githubUrl ?? ""}
												location="hero"
												target="_blank"
												className="inline-block"
											>
												<Tag
													icon={<Sparkles />}
													variant="default"
													className="cursor-pointer transition-colors hover:border-foreground/25"
												>
													Open source · Self-host or cloud
												</Tag>
											</TrackedLink>
										</AnimatedGroup>

										<AnimatedGroup variants={transitionVariants}>
											<h1 className="max-w-4xl mx-auto lg:mx-0 text-balance text-5xl sm:text-6xl font-bold tracking-tight leading-[1.05]">
												Deploy anything. <br />
												<span className="text-muted-foreground">
													Own everything.
												</span>
											</h1>
											<p className="mx-auto lg:mx-0 mt-8 max-w-xl text-lg text-muted-foreground leading-relaxed">
												Git push to ship apps and agents on one serverless
												cloud. Databases, queues, cron, durable workflows,
												sandboxes, and built-in model access are included, and
												the whole platform is open source, so you can run it
												yourself or build your own platform on top.
											</p>
										</AnimatedGroup>

										<AnimatedGroup
											variants={{
												container: {
													visible: {
														transition: {
															staggerChildren: 0.05,
															delayChildren: 0.75,
														},
													},
												},
												...transitionVariants,
											}}
											className="flex flex-wrap items-center justify-center lg:justify-start gap-4"
										>
											<Button
												key={1}
												size="lg"
												render={
													<AuthLink
														href="/signup"
														ctaId="deploy_first_agent"
														ctaLocation="hero"
														ctaText="Deploy your first agent"
													/>
												}
											>
												<span className="text-nowrap">
													Deploy your first agent
												</span>
											</Button>
											<Button
												key={2}
												size="lg"
												variant="outline"
												render={
													<TrackedLink
														href={config.docsUrl ?? ""}
														location="hero"
														destination="docs"
														linkText="Read the docs"
														target="_blank"
													/>
												}
											>
												<span className="text-nowrap">Read the docs</span>
											</Button>
										</AnimatedGroup>
									</div>

									{/* Column 2: Code Example */}
									<div className="hidden lg:block relative">
										<AnimatedGroup
											variants={{
												container: {
													visible: {
														transition: {
															staggerChildren: 0.05,
															delayChildren: 0.5,
														},
													},
												},
												...transitionVariants,
											}}
										>
											<FeatureConsole autoLoop />
										</AnimatedGroup>
									</div>
								</div>
							</div>

							<AnimatedGroup
								variants={{
									container: {
										visible: {
											transition: {
												staggerChildren: 0.05,
												delayChildren: 0.75,
											},
										},
									},
									...transitionVariants,
								}}
							>
								<div className="relative -mr-56 mt-8 overflow-hidden px-2 sm:mr-0 sm:mt-12 md:mt-20">
									<div
										aria-hidden
										className="bg-gradient-to-b to-background absolute inset-0 z-10 from-transparent from-35%"
									/>
									<div className="bg-background relative mx-auto max-w-6xl overflow-hidden rounded-none border p-2 shadow-sm">
										<Image
											className="bg-background relative hidden rounded-none dark:block"
											src={heroDark}
											alt="Ploy dashboard showing a project's live URLs, current deployment, and recent build history"
										/>
										<Image
											className="z-2 border-border/25 relative rounded-none border dark:hidden"
											src={heroLight}
											alt="Ploy dashboard showing a project's live URLs, current deployment, and recent build history"
										/>
									</div>
								</div>
							</AnimatedGroup>
						</div>
					</section>
				</main>
			)}
		</>
	);
}
